<script setup lang="ts">
import { computed } from 'vue'
import Drawer from '@uiKit/elements/Drawer.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import AppImage from '@uiKit/elements/Image.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import Message from '@uiKit/elements/Message.vue'
import Card from '@uiKit/components/Card.vue'
import { formatKey } from '@shared/utils/stringUtils'
import type { Transaction } from '../types/transactionTypes'
import type { TransactionDetail } from '../types/transactionDetailTypes'
import {
  TRANSACTION_STATUS_CONFIG,
  TRANSACTION_TYPE_THEME,
  type TypeTheme,
} from './transactionDetailTheme'

interface Props {
  transaction: Transaction | null
  open: boolean
  detail: TransactionDetail | null
  detailLoading: boolean
  detailError: unknown
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
}>()

const theme = computed<TypeTheme>(() =>
  props.transaction ? TRANSACTION_TYPE_THEME[props.transaction.type] : TRANSACTION_TYPE_THEME.deposit,
)

const statusConfig = computed(() =>
  props.transaction ? TRANSACTION_STATUS_CONFIG[props.transaction.status] : null,
)

const formatAmount = (amount: string | null, currency: string | null): string => {
  if (!amount) return '—'
  return currency ? `${currency} ${amount}` : amount
}

// Detail sections that render only when the matching slice is present on
// the response. The drawer iterates this list rather than duplicating the
// same key/value card markup four times.
const detailSections = computed(() => [
  { heading: 'Withdrawal details', entries: props.detail?.withdrawalDetails },
  { heading: 'Investment details', entries: props.detail?.investmentDetails },
  { heading: 'Deposit details', entries: props.detail?.depositDetails },
  { heading: 'Rental details', entries: props.detail?.rentalDetails },
])

const referenceIdLabel = computed(() => props.detail?.referenceId || props.transaction?.referenceId || null)
</script>

<template>
  <Drawer
    :model-value="open"
    position="right"
    width="520px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && emit('close')"
  >
    <template #header>
      <Box align="center" justify="between" gap="2" class="tdd__header">
        <Text type="page-title">Transaction details</Text>
        <Box
          is="button"
          align="center"
          justify="center"
          class="tdd__close"
          type="button"
          aria-label="Close"
          @click="emit('close')"
        >
          <PiIcon name="times" />
        </Box>
      </Box>
    </template>

    <Box direction="col" class="tdd__shell">
      <Box direction="col" gap="3" class="tdd__main">
        <!-- Loading -->
        <Box v-if="detailLoading" direction="col" gap="3">
          <Skeleton animation="wave" width="100%" height="160px" border-radius="14px" />
          <Skeleton animation="wave" width="70%" height="22px" border-radius="6px" />
          <Skeleton animation="wave" width="100%" height="140px" border-radius="14px" />
          <Skeleton animation="wave" width="100%" height="120px" border-radius="14px" />
        </Box>

        <!-- Error -->
        <Message
          v-else-if="detailError"
          color="danger"
          variant="outlined"
          message="We couldn't load this transaction. Try again later."
        />

        <!-- Data -->
        <template v-else-if="detail && transaction">
          <!-- Hero card -->
          <Card variant="elevated" padding="4" direction="col" gap="3" align="center">
            <Box
              align="center"
              justify="center"
              class="tdd__icon-circle"
              :style="{
                background: theme.iconBg,
                border: `2px solid ${theme.iconBorder}`,
              }"
            >
              <AppImage
                v-if="theme.imageSrc"
                :src="theme.imageSrc"
                :alt="transaction.category"
                image-class="tdd__type-icon"
              />
              <PiIcon
                v-else
                :name="theme.icon"
                :style="{ color: theme.iconColor, fontSize: '28px' }"
              />
            </Box>

            <Text
              type="display"
              align="center"
              is="span"
              class="tdd__amount"
              :style="{ color: theme.amountColor }"
            >
              {{ formatAmount(detail.amount, detail.currencyCode) }}
            </Text>
            <Text type="heading" align="center" is="span">
              {{ detail.displayTitle || '—' }}
            </Text>
            <Text
              v-if="detail.transactionReason"
              type="body-sm"
              color="muted"
              align="center"
              is="span"
            >
              {{ formatKey(detail.transactionReason) }}
            </Text>
          </Card>

          <!-- Transaction details card -->
          <Card variant="elevated" padding="4" direction="col" gap="3">
            <Text type="caption" color="muted" transform="uppercase">Transaction details</Text>
            <Box direction="col" gap="2">
              <Box align="center" justify="between" gap="3">
                <Text type="body-sm" color="muted">Reference ID</Text>
                <Text type="body-sm" weight="semibold" align="right" truncate>
                  {{ referenceIdLabel || '—' }}
                </Text>
              </Box>
              <Box class="tdd__divider" />
              <Box align="center" justify="between" gap="3">
                <Text type="body-sm" color="muted">Date &amp; Time</Text>
                <Text type="body-sm" weight="semibold" align="right">
                  {{ detail.createdAt || '—' }}
                </Text>
              </Box>
              <template v-if="detail.transactionType">
                <Box class="tdd__divider" />
                <Box align="center" justify="between" gap="3">
                  <Text type="body-sm" color="muted">Type</Text>
                  <Text type="body-sm" weight="semibold" align="right">
                    {{ formatKey(detail.transactionType) }}
                  </Text>
                </Box>
              </template>
              <Box v-if="statusConfig" class="tdd__divider" />
              <Box v-if="statusConfig" align="center" justify="between" gap="3">
                <Text type="body-sm" color="muted">Status</Text>
                <Box
                  align="center"
                  gap="1"
                  class="tdd__chip"
                  :style="{ background: statusConfig.bg }"
                >
                  <PiIcon
                    :name="statusConfig.icon"
                    :style="{ color: statusConfig.color, fontSize: '11px' }"
                  />
                  <Text
                    type="micro"
                    is="span"
                    class="tdd__chip-label"
                    :style="{ color: statusConfig.color }"
                  >
                    {{ statusConfig.label }}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Card>

          <!-- Amount details card -->
          <Card
            v-if="detail.amount || detail.balanceBefore !== null || detail.balanceAfter !== null"
            variant="elevated"
            padding="4"
            direction="col"
            gap="3"
          >
            <Text type="caption" color="muted" transform="uppercase">Amount details</Text>
            <Box direction="col" gap="2">
              <Box align="center" justify="between" gap="3">
                <Text type="body-sm" color="muted">Amount</Text>
                <Text
                  type="heading"
                  weight="bold"
                  align="right"
                  :style="{ color: theme.amountColor }"
                >
                  {{ formatAmount(detail.amount, detail.currencyCode) }}
                </Text>
              </Box>
              <template v-if="detail.balanceBefore !== null">
                <Box class="tdd__divider" />
                <Box align="center" justify="between" gap="3">
                  <Text type="body-sm" color="muted">Balance before</Text>
                  <Text type="body-sm" weight="semibold" align="right">
                    {{ formatAmount(detail.balanceBefore, detail.currencyCode) }}
                  </Text>
                </Box>
              </template>
              <template v-if="detail.balanceAfter !== null">
                <Box class="tdd__divider" />
                <Box align="center" justify="between" gap="3">
                  <Text type="body-sm" color="muted">Balance after</Text>
                  <Text type="body-sm" weight="semibold" align="right">
                    {{ formatAmount(detail.balanceAfter, detail.currencyCode) }}
                  </Text>
                </Box>
              </template>
            </Box>
          </Card>

          <!-- Optional per-leg detail cards -->
          <template v-for="section in detailSections" :key="section.heading">
            <Card v-if="section.entries" variant="elevated" padding="4" direction="col" gap="3">
              <Text type="caption" color="muted" transform="uppercase">{{ section.heading }}</Text>
              <Box direction="col" gap="2">
                <template v-for="(value, key, index) in section.entries" :key="key">
                  <template v-if="value !== null && value !== undefined">
                    <Box v-if="index > 0" class="tdd__divider" />
                    <Box align="center" justify="between" gap="3">
                      <Text type="body-sm" color="muted">{{ formatKey(key) }}</Text>
                      <Text type="body-sm" weight="semibold" align="right" truncate>
                        {{ value }}
                      </Text>
                    </Box>
                  </template>
                </template>
              </Box>
            </Card>
          </template>
        </template>
      </Box>
    </Box>
  </Drawer>
</template>

<style scoped>
.tdd__header {
  width: 100%;
}

.tdd__close {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  cursor: pointer;
  color: var(--p-text-muted-color);
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.tdd__close:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
}

.tdd__shell {
  min-height: 100%;
}

.tdd__main {
  flex: 1;
  padding: 1.25rem 1.5rem 1.5rem;
}

.tdd__icon-circle {
  display: inline-flex;
  width: 72px;
  height: 72px;
  border-radius: 50%;
}

.tdd__icon-circle :deep(.tdd__type-icon) {
  width: 36px;
  height: 36px;
  display: block;
}

.tdd__amount {
  line-height: 1.2;
  letter-spacing: -0.4px;
}

.tdd__divider {
  height: 1px;
  background: var(--p-content-border-color);
}

.tdd__chip {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
}

.tdd__chip-label {
  letter-spacing: 0.04em;
}
</style>
